import { useEffect, useRef } from 'react'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'
import { Motion } from '@/lib/motion/lazy-motion'
import FullScreenCover from '@/components/ui/FullScreenCover'
import Link from '@/components/ui/Link'
import DiscoverMenu from './menus/DiscoverMenu'
import { ComparisonList, CategoryList, CreateComparison, AppList } from './menus/ComparisonsMenu'
import CoursesMenu from './menus/CoursesMenu'
import DealsMenu from './menus/DealsMenu'
import StacksMenu from './menus/StacksMenu'
import Button from '@/components/ui/Button'
import Pill from '@/components/ui/Pill'
import { Icon } from '@/icons/Icon'
import { LinkProps } from '@/types/app'
import { easeMedium } from '@/lib/transitions'
import { cn, hapticTap } from '@/lib/utils'
import { useHeaderContext } from '@/context/HeaderContext'
import { getNavCta } from '@/components/structure/header/nav-cta'
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn'

type Links = {
  links: LinkProps[]
  isTraffic: boolean
}

type SectionProps = {
  link: LinkProps
  closeMenu: () => void
}

export default function MobileNavigation({ links, isTraffic }: Links) {
  const { theme, showMenu, setShowMenu, stage } = useHeaderContext()

  const prevStage = useRef(stage)
  const direction: Direction = prevStage.current < stage ? 'forward' : 'back'

  useEffect(() => {
    const timeout = setTimeout(() => {
      prevStage.current = stage
    }, 100)
    return () => clearTimeout(timeout)
  }, [stage])

  type Direction = 'back' | 'forward'

  const variants = {
    initial: (direction: Direction) => ({
      x: direction === 'forward' ? '100%' : '-100%',
    }),
    target: {
      x: '0%',
    },
    exit: (direction: Direction) => ({
      x: direction === 'forward' ? '-100%' : '100%',
    }),
  }

  return (
    <FullScreenCover
      trigger={
        <Button
          onClick={() => {
            setShowMenu(!showMenu)
            hapticTap()
          }}
          style={theme === 'dark' ? 'invert' : 'input'}
          size="large"
          aria-label={showMenu ? 'Close menu' : 'Open menu'}
        >
          <Icon name="menu" size={16} />
        </Button>
      }
      isVisible={showMenu}
      close={() => {
        setShowMenu(false)
        hapticTap()
      }}
    >
      <div className="relative min-h-screen w-full">
        <Motion>
          <AnimatePresence custom={direction} initial={false}>
            <m.div
              key={stage}
              variants={variants}
              custom={direction}
              initial="initial"
              animate="target"
              exit="exit"
              transition={easeMedium}
              className="absolute left-0 top-0 flex w-full flex-col"
            >
              {stage === 0 ? (
                <Root links={links} isTraffic={isTraffic} />
              ) : stage === 1 ? (
                <CategoryList delay={true} />
              ) : stage === 2 ? (
                <AppList />
              ) : null}
            </m.div>
          </AnimatePresence>
        </Motion>
      </div>
    </FullScreenCover>
  )
}

export function Root({ links, isTraffic }: Links) {
  const { setShowMenu } = useHeaderContext()
  const navCta = getNavCta(isTraffic)
  const authState = useIsLoggedIn()

  const closeMenu = () => setShowMenu(false)

  return (
    <div className="flex flex-col gap-3">
      <ul className="mb-3 flex flex-col gap-2.5 text-13 font-medium text-dark-950 dark:text-white">
        {links.map((link, index) => (
          <Section key={index} link={link} closeMenu={closeMenu} />
        ))}
      </ul>

      <Link
        href={navCta.href}
        aria-label={navCta.ariaLabel}
        onClick={closeMenu}
        className="w-full px-3"
      >
        <Pill as="span" style="default" size="mobile" className="rounded-lg">
          {navCta.label}
        </Pill>
      </Link>

      {authState !== 'authenticated' && (
        <Link
          href="/login?signup=true"
          aria-label="Sign up"
          onClick={closeMenu}
          className="w-full px-3"
        >
          <Pill as="span" style="outline" size="mobile" className="rounded-lg">
            Sign up
          </Pill>
        </Link>
      )}

      {authState === 'authenticated' && (
        <button
          type="button"
          onClick={() => window.location.assign('/logout')}
          aria-label="Log out"
          className="w-full px-3"
        >
          <Pill as="span" style="outline" size="mobile" className="rounded-lg">
            Log out
          </Pill>
        </button>
      )}
    </div>
  )
}

export function Section({ link, closeMenu }: SectionProps) {
  const { focus, setFocus } = useHeaderContext()
  const { label, url, target, menu } = link
  const renderMenu = (type: string | null) => {
    switch (type) {
      case 'Discover':
        return <DiscoverMenu />
      case 'Deals':
        return <DealsMenu />
      case 'Stacks':
        return <StacksMenu />
      case 'Comparisons':
        return (
          <>
            <ComparisonList />
            <CreateComparison />
          </>
        )
      case 'Courses':
        return <CoursesMenu />
      default:
        return null
    }
  }

  const sharedContent = (
    <div className="group flex h-10 w-full items-center justify-between gap-3 px-6">
      <div
        className={cn(
          'text-16 transition-colors group-hover:text-black dark:group-hover:text-white',
          focus === label ? 'text-primary-500' : 'text-gray-500',
        )}
      >
        {label}
      </div>
      {menu && (
        <div
          className={cn(
            'pr-1 transition-all duration-300 ease-custom group-hover:text-black dark:group-hover:text-white',
            focus === label ? 'rotate-90 text-primary-500' : '-rotate-90 text-gray-400',
          )}
        >
          <Icon name="chevron-left" size={12} />
        </div>
      )}
    </div>
  )

  const linkClass = 'relative flex h-10 items-center cursor-pointer'

  const handleFocus = () => {
    if (focus === label) {
      setFocus(null)
    } else {
      setFocus(label)
    }
  }

  const isOpen = menu && focus === label

  return (
    <div className="relative">
      {url && !menu ? (
        <Link
          href={url}
          target={target}
          state="disabled"
          className={linkClass}
          onClick={() => closeMenu()}
        >
          {sharedContent}
        </Link>
      ) : (
        <div onClick={handleFocus} className={linkClass}>
          {sharedContent}
        </div>
      )}

      <div
        className={cn(
          // grid-cols-1 (minmax(0, 1fr)) keeps the implicit column from
          // growing to fit nowrap/truncated content inside the menu (e.g.
          // CoursesMenu's course names) wider than the container - without
          // it, grid's default auto column sizes to content's min-content
          // width instead of the available space.
          'grid grid-cols-1 overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="min-h-0">
          <div className="mt-2 border-y border-gray-200 dark:border-white/10">
            {renderMenu(label)}

            {url && label !== 'Discover' && (
              <Link
                href={`${url}#all`}
                onClick={closeMenu}
                className="group mb-6 flex items-center gap-2 px-5 text-13 text-gray-500 transition-colors hover:text-gray-950 dark:hover:text-white md:hidden"
              >
                View all {label.toLowerCase()}
                <div className="rotate-180 transition-colors">
                  <Icon name="chevron-left" size={12} />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
